import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Save, Loader2, CheckCircle2, Upload, Trash2,
  ArrowUp, ArrowDown, Plus, GalleryHorizontal,
} from "lucide-react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import HomeStrip from "../components/HomeStrip";
import HomepageBanner from "../components/HomepageBanner";

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/uploads`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!r.ok) throw new Error("Upload failed");
  const data = await r.json();
  return data.url;
}

function uid() {
  return "strip_" + Math.random().toString(36).slice(2, 10);
}

function newSlide() {
  return { id: uid(), image_url: "", link_url: "", alt: "", open_in_new_tab: true };
}

export default function AdminHomepageStrip() {
  const [enabled, setEnabled] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const [intervalMs, setIntervalMs] = useState(5000);
  const [showOnDesktop, setShowOnDesktop] = useState(true);
  const [showOnMobile, setShowOnMobile] = useState(true);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/settings/homepage-strip")
      .then((r) => {
        const d = r.data || {};
        setEnabled(!!d.enabled);
        setAutoRotate(d.auto_rotate !== false);
        setIntervalMs(d.interval_ms || 5000);
        setShowOnDesktop(d.show_on_desktop !== false);
        setShowOnMobile(d.show_on_mobile !== false);
        const list = Array.isArray(d.slides) ? d.slides : [];
        setSlides(list.map((s) => (s.id ? s : { ...s, id: uid() })));
      })
      .catch((e) => setErr(formatApiErrorDetail(e)))
      .finally(() => setLoading(false));
  }, []);

  const update = (id, patch) =>
    setSlides((s) => s.map((sl) => (sl.id === id ? { ...sl, ...patch } : sl)));

  const remove = (id) => setSlides((s) => s.filter((sl) => sl.id !== id));

  const move = (id, delta) => {
    setSlides((s) => {
      const i = s.findIndex((sl) => sl.id === id);
      if (i < 0) return s;
      const j = i + delta;
      if (j < 0 || j >= s.length) return s;
      const next = s.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.put("/settings/homepage-strip", {
        enabled,
        auto_rotate: autoRotate,
        interval_ms: intervalMs,
        show_on_desktop: showOnDesktop,
        show_on_mobile: showOnMobile,
        slides,
      });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setErr(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] p-10 text-[var(--brand-ink)]">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]">
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1 text-sm text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
          data-testid="admin-strip-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </RouterLink>

        <h1 className="mt-4 font-display text-3xl font-black sm:text-4xl">
          Homepage strip
        </h1>
        <p className="mt-1 text-sm text-[var(--brand-body)]">
          A full-width banner strip between the ticker and the "LAteST from
          LAST" heading — <strong>mobile only</strong>. Add one or more image
          slides, each linking to a URL of your choice, and choose whether
          they auto-rotate. Desktop visitors see the separate Homepage
          banner below instead.
        </p>

        {err ? (
          <div
            className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700"
            data-testid="admin-strip-error"
          >
            {err}
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center gap-6 rounded-md border border-black/10 bg-white p-4">
          <label className="inline-flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="admin-strip-enabled"
            />
            Show strip on home page
          </label>
          <label className="inline-flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={showOnDesktop}
              onChange={(e) => setShowOnDesktop(e.target.checked)}
              data-testid="admin-strip-show-desktop"
            />
            Show on desktop
          </label>
          <label className="inline-flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={showOnMobile}
              onChange={(e) => setShowOnMobile(e.target.checked)}
              data-testid="admin-strip-show-mobile"
            />
            Show on mobile
          </label>
          <label className="inline-flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={autoRotate}
              onChange={(e) => setAutoRotate(e.target.checked)}
              data-testid="admin-strip-auto-rotate"
            />
            Auto-rotate slides
          </label>
          {autoRotate ? (
            <label className="inline-flex items-center gap-2 text-sm">
              Every
              <input
                type="number"
                min={2}
                max={20}
                value={Math.round(intervalMs / 1000)}
                onChange={(e) =>
                  setIntervalMs(Math.min(20000, Math.max(2000, Number(e.target.value || 5) * 1000)))
                }
                className="w-16 rounded-sm border border-black/15 bg-white px-2 py-1 text-sm outline-none focus:border-[#0A0F17]"
                data-testid="admin-strip-interval"
              />
              seconds
            </label>
          ) : null}
        </div>

        <div className="mt-6 space-y-4" data-testid="admin-strip-list">
          {slides.length === 0 ? (
            <div className="rounded-md border border-dashed border-black/15 bg-white p-6 text-sm italic text-[var(--brand-body)]">
              No slides yet — add an image to get started.
            </div>
          ) : null}

          {slides.map((s, i) => (
            <SlideCard
              key={s.id}
              slide={s}
              index={i}
              total={slides.length}
              onChange={(patch) => update(s.id, patch)}
              onRemove={() => remove(s.id)}
              onMoveUp={() => move(s.id, -1)}
              onMoveDown={() => move(s.id, +1)}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => setSlides((s) => [...s, newSlide()])}
          className="mt-4 inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 text-sm font-semibold hover:border-[#0A0F17]"
          data-testid="admin-strip-add-slide"
        >
          <Plus className="h-4 w-4" /> <GalleryHorizontal className="h-4 w-4" /> Add slide
        </button>

        {enabled && slides.some((s) => s.image_url) ? (
          <div className="mt-8">
            <p className="label-kicker text-[var(--brand-body)]">Live preview</p>
            <div className="mt-2">
              <HomeStrip />
            </div>
          </div>
        ) : null}

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            data-testid="admin-strip-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </button>
          {savedFlash ? (
            <span
              className="inline-flex items-center gap-1 text-sm text-emerald-700"
              data-testid="admin-strip-saved-flash"
            >
              <CheckCircle2 className="h-4 w-4" /> Saved
            </span>
          ) : null}
        </div>

        <hr className="my-12 border-black/10" />

        <HomepageBannerSection />
      </main>
    </div>
  );
}

function SlideCard({ slide, index, total, onChange, onRemove, onMoveUp, onMoveDown, testIdPrefix = "admin-strip" }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange({ image_url: url });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="rounded-md border border-black/10 bg-white p-4"
      data-testid={`${testIdPrefix}-slide-${index}`}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <GalleryHorizontal className="h-4 w-4" /> Slide
          <span className="text-xs font-normal text-[var(--brand-body)]">#{index + 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="rounded p-1 hover:bg-black/5 disabled:opacity-30"
            title="Move up"
            data-testid={`${testIdPrefix}-slide-up-${index}`}
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="rounded p-1 hover:bg-black/5 disabled:opacity-30"
            title="Move down"
            data-testid={`${testIdPrefix}-slide-down-${index}`}
          >
            <ArrowDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-red-600 hover:bg-red-50"
            title="Delete slide"
            data-testid={`${testIdPrefix}-slide-remove-${index}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">
              Image
            </label>
            <input
              type="text"
              value={slide.image_url || ""}
              onChange={(e) => onChange({ image_url: e.target.value })}
              placeholder="/api/files/... or https://"
              className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              data-testid={`${testIdPrefix}-image-url-${index}`}
            />
            <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-md border border-black/15 px-3 py-1.5 text-xs font-semibold hover:border-[#0A0F17]">
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              Upload image
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onFile(e.target.files && e.target.files[0])}
              />
            </label>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">
              Link URL
            </label>
            <input
              type="text"
              value={slide.link_url || ""}
              onChange={(e) => onChange({ link_url: e.target.value })}
              placeholder="https://... or /page/..."
              className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              data-testid={`${testIdPrefix}-link-url-${index}`}
            />
          </div>
          <label className="inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={slide.open_in_new_tab !== false}
              onChange={(e) => onChange({ open_in_new_tab: e.target.checked })}
              data-testid={`${testIdPrefix}-new-tab-${index}`}
            />
            Open in a new tab
          </label>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">
              Alt text
            </label>
            <input
              type="text"
              value={slide.alt || ""}
              onChange={(e) => onChange({ alt: e.target.value })}
              placeholder="Describes the image for screen readers"
              className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
          </div>
        </div>
        <div>
          <p className="label-kicker text-[var(--brand-body)]">Preview</p>
          <div className="mt-2 overflow-hidden rounded-md border border-black/10">
            {slide.image_url ? (
              <img src={slide.image_url} alt={slide.alt || ""} className="aspect-[2000/409] w-full object-cover" />
            ) : (
              <p className="p-4 text-xs italic text-[var(--brand-body)]">No image set yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function HomepageBannerSection() {
  const [enabled, setEnabled] = useState(false);
  const [displayMode, setDisplayMode] = useState("rotate");
  const [selectedSlideId, setSelectedSlideId] = useState("");
  const [intervalMs, setIntervalMs] = useState(5000);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/settings/homepage-banner")
      .then((r) => {
        const d = r.data || {};
        setEnabled(!!d.enabled);
        setDisplayMode(d.display_mode === "single" ? "single" : "rotate");
        setSelectedSlideId(d.selected_slide_id || "");
        setIntervalMs(d.interval_ms || 5000);
        const list = Array.isArray(d.slides) ? d.slides : [];
        setSlides(list.map((s) => (s.id ? s : { ...s, id: uid() })));
      })
      .catch((e) => setErr(formatApiErrorDetail(e)))
      .finally(() => setLoading(false));
  }, []);

  const update = (id, patch) =>
    setSlides((s) => s.map((sl) => (sl.id === id ? { ...sl, ...patch } : sl)));

  const remove = (id) => setSlides((s) => s.filter((sl) => sl.id !== id));

  const move = (id, delta) => {
    setSlides((s) => {
      const i = s.findIndex((sl) => sl.id === id);
      if (i < 0) return s;
      const j = i + delta;
      if (j < 0 || j >= s.length) return s;
      const next = s.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.put("/settings/homepage-banner", {
        enabled,
        display_mode: displayMode,
        selected_slide_id: selectedSlideId,
        interval_ms: intervalMs,
        slides,
      });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setErr(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader2 className="h-5 w-5 animate-spin" data-testid="admin-banner-loading" />;
  }

  return (
    <div data-testid="admin-banner-section">
      <h2 className="font-display text-2xl font-black sm:text-3xl">
        Homepage banner (desktop only)
      </h2>
      <p className="mt-1 text-sm text-[var(--brand-body)]">
        Same slot, same spot — between the ticker and the "LAteST from LAST"
        heading — but shown to <strong>desktop</strong> visitors only. Add one
        or more banners, then either pick one specific banner to always show,
        or let them auto-rotate.
      </p>

      {err ? (
        <div
          className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700"
          data-testid="admin-banner-error"
        >
          {err}
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center gap-6 rounded-md border border-black/10 bg-white p-4">
        <label className="inline-flex items-center gap-2 text-sm font-semibold">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="admin-banner-enabled"
          />
          Show banner on home page
        </label>
        <label className="inline-flex items-center gap-2 text-sm font-semibold">
          <input
            type="radio"
            name="banner-display-mode"
            checked={displayMode === "rotate"}
            onChange={() => setDisplayMode("rotate")}
            data-testid="admin-banner-mode-rotate"
          />
          Auto-rotate through all
        </label>
        <label className="inline-flex items-center gap-2 text-sm font-semibold">
          <input
            type="radio"
            name="banner-display-mode"
            checked={displayMode === "single"}
            onChange={() => setDisplayMode("single")}
            data-testid="admin-banner-mode-single"
          />
          Show one specific banner
        </label>
        {displayMode === "rotate" ? (
          <label className="inline-flex items-center gap-2 text-sm">
            Every
            <input
              type="number"
              min={2}
              max={20}
              value={Math.round(intervalMs / 1000)}
              onChange={(e) =>
                setIntervalMs(Math.min(20000, Math.max(2000, Number(e.target.value || 5) * 1000)))
              }
              className="w-16 rounded-sm border border-black/15 bg-white px-2 py-1 text-sm outline-none focus:border-[#0A0F17]"
              data-testid="admin-banner-interval"
            />
            seconds
          </label>
        ) : (
          <label className="inline-flex items-center gap-2 text-sm">
            Which one:
            <select
              value={selectedSlideId}
              onChange={(e) => setSelectedSlideId(e.target.value)}
              className="rounded-sm border border-black/15 bg-white px-2 py-1.5 text-sm outline-none focus:border-[#0A0F17]"
              data-testid="admin-banner-selected-slide"
            >
              <option value="">First available</option>
              {slides.map((s, i) => (
                <option key={s.id} value={s.id}>
                  {s.alt || `Slide #${i + 1}`}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="mt-6 space-y-4" data-testid="admin-banner-list">
        {slides.length === 0 ? (
          <div className="rounded-md border border-dashed border-black/15 bg-white p-6 text-sm italic text-[var(--brand-body)]">
            No banners yet — add an image to get started.
          </div>
        ) : null}

        {slides.map((s, i) => (
          <SlideCard
            key={s.id}
            slide={s}
            index={i}
            total={slides.length}
            onChange={(patch) => update(s.id, patch)}
            onRemove={() => remove(s.id)}
            onMoveUp={() => move(s.id, -1)}
            onMoveDown={() => move(s.id, +1)}
            testIdPrefix="admin-banner"
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => setSlides((s) => [...s, newSlide()])}
        className="mt-4 inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 text-sm font-semibold hover:border-[#0A0F17]"
        data-testid="admin-banner-add-slide"
      >
        <Plus className="h-4 w-4" /> <GalleryHorizontal className="h-4 w-4" /> Add banner
      </button>

      {enabled && slides.some((s) => s.image_url) ? (
        <div className="mt-8">
          <p className="label-kicker text-[var(--brand-body)]">
            Live preview (desktop only — won't show here on a narrow screen)
          </p>
          <div className="mt-2">
            <HomepageBanner />
          </div>
        </div>
      ) : null}

      <div className="mt-8 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
          data-testid="admin-banner-save"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save changes
        </button>
        {savedFlash ? (
          <span
            className="inline-flex items-center gap-1 text-sm text-emerald-700"
            data-testid="admin-banner-saved-flash"
          >
            <CheckCircle2 className="h-4 w-4" /> Saved
          </span>
        ) : null}
      </div>
    </div>
  );
}
